import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cityApi, CitiesListParams, CreateCityData, UpdateCityData } from '../../services/cityApi';

export interface City {
    id: number;
    name: string;
    code: string;
    zone: string;
    status: number;
    created_at: string;
    updated_at: string;
}

export interface CitiesState {
    cities: City[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: {
        status: string;
        query: string;
        zone: string;
    };
    zones: string[];
    loading: boolean;
    error: string | null;
}

const initialState: CitiesState = {
    cities: [],
    pagination: null,
    filters: {
        status: '',
        query: '',
        zone: '',
    },
    zones: [],
    loading: false,
    error: null,
};

export const fetchCities = createAsyncThunk(
    'cities/fetchCities',
    async (params: CitiesListParams = {}) => {
        const response = await cityApi.getCities(params);
        return response;
    }
);

export const fetchZones = createAsyncThunk(
    'cities/fetchZones',
    async () => {
        const response = await cityApi.getZones();
        return response;
    }
);

export const createCity = createAsyncThunk(
    'cities/createCity',
    async (cityData: CreateCityData) => {
        const response = await cityApi.createCity(cityData);
        return response;
    }
);

export const updateCity = createAsyncThunk(
    'cities/updateCity',
    async ({ id, data }: { id: number; data: UpdateCityData }) => {
        const response = await cityApi.updateCity(id, data);
        return response;
    }
);

export const toggleCityStatus = createAsyncThunk(
    'cities/toggleCityStatus',
    async (id: number) => {
        const response = await cityApi.toggleCityStatus(id);
        return response;
    }
);

const citySlice = createSlice({
    name: 'cities',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                status: '',
                query: '',
                zone: '',
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCities.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCities.fulfilled, (state, action) => {
                state.loading = false;
                state.cities = action.payload.cities;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchCities.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch cities';
            })
            .addCase(fetchZones.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchZones.fulfilled, (state, action) => {
                state.loading = false;
                state.zones = action.payload;
            })
            .addCase(fetchZones.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch zones';
            })
            .addCase(createCity.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCity.fulfilled, (state, action) => {
                state.loading = false;
                state.cities.unshift(action.payload);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createCity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create city';
            })
            .addCase(updateCity.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCity.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.cities.findIndex(city => city.id === action.payload.id);
                if (index !== -1) {
                    state.cities[index] = action.payload;
                }
            })
            .addCase(updateCity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update city';
            })
            .addCase(toggleCityStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleCityStatus.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.cities.findIndex(city => city.id === action.payload.id);
                if (index !== -1) {
                    state.cities[index].status = action.payload.status;
                }
            })
            .addCase(toggleCityStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to toggle city status';
            });
    },
});

export const { clearError, setFilters, clearFilters } = citySlice.actions;
export default citySlice.reducer;
